// Capacity v1 (owner, 2026-10-12/13): free hours per person per week, worked
// out by capacity_overview (20261013120000_capacity_v1.sql) from contract hours
// × working days, less approved leave; pending leave is tentative. Who appears
// follows policy.capacity.visibility, inside the function. Capacity and Talent
// both read it.
import { supabase } from './supabase';
import { workingDayList, type Workdays } from './workdays';

export type SkillLevel = 1 | 2 | 3 | 4;
export type PersonSkill = { skill_id: string | null; name: string; level: SkillLevel };
export type CapacityWeek = {
  /** 'YYYY-MM-DD', the first day of the org's week. */
  week_start: string;
  work_days: number;
  standard_hours: number;
  away_hours: number;
  tentative_hours: number;
  free_hours: number;
};
export type CapacityPerson = {
  profile_id: string;
  full_name: string | null;
  title: string | null;
  department: string | null;
  freelance: boolean;
  day_hours: number;
  /** Highest level first; archived skills are already left out. */
  skills: PersonSkill[];
  weeks: CapacityWeek[];
};

// The level is the colour (owner, 2026-10-13), from the category slots, never
// sage/amber/rose: a Learning skill is not a problem. The name always shows
// beside the colour, which is a mark, never text (tokens.css).
export const LEVELS: Record<SkillLevel, { label: string; color: string }> = {
  1: { label: 'Learning', color: 'var(--hf-cat-8)' },
  2: { label: 'Capable', color: 'var(--hf-cat-2)' },
  3: { label: 'Strong', color: 'var(--hf-cat-4)' },
  4: { label: 'Expert', color: 'var(--hf-cat-3)' },
};
export const LEVEL_IDS = [1, 2, 3, 4] as const;

/** Rounded for display: "7.5", "40". */
export const hrs = (n: number): string => String(Math.round(n * 10) / 10);

export type CapacityVisibility = 'chain' | 'chain_hr_admin' | 'everyone';
/** capacity_visibility()'s default when policy.capacity is unset. */
export const DEFAULT_CAPACITY_VISIBILITY: CapacityVisibility = 'chain_hr_admin';

export const fetchCapacity = async (
  weeks = 12,
): Promise<{ data: CapacityPerson[]; failed: boolean }> => {
  if (!supabase) return { data: [], failed: false };
  const { data, error } = await supabase.rpc('capacity_overview', { p_weeks: weeks });
  return { data: (data as CapacityPerson[] | null) ?? [], failed: !!error };
};

/** One finished week, totalled over the people the caller may see (capacity_trend). */
export type CapacityTrendWeek = {
  week_start: string;
  people: number;
  standard_hours: number;
  away_hours: number;
  worked_hours: number;
  allocated_hours: number;
  billable_hours: number;
};

/**
 * The last `weeks` FINISHED weeks from the weekly snapshots
 * (20261015230000_weekly_snapshots.sql), oldest first. Empty without
 * capacity:view; the same who-sees-whom rule as capacity_overview.
 */
export const fetchCapacityTrend = async (weeks = 12): Promise<CapacityTrendWeek[]> => {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('capacity_trend', { p_weeks: weeks });
  if (error) return [];
  return ((data as CapacityTrendWeek[] | null) ?? []).map((w) => ({
    ...w,
    people: Number(w.people),
    standard_hours: Number(w.standard_hours),
    away_hours: Number(w.away_hours),
    worked_hours: Number(w.worked_hours),
    allocated_hours: Number(w.allocated_hours),
    billable_hours: Number(w.billable_hours),
  }));
};

/** Average worked hours per person in a week; null when nobody is counted. */
export const avgHoursPerPerson = (w: CapacityTrendWeek): number | null =>
  w.people > 0 ? Math.round((w.worked_hours / w.people) * 10) / 10 : null;

/** Project hours ÷ standard hours, as a whole %; null with no standard. */
export const weekUtilisation = (w: CapacityTrendWeek): number | null =>
  w.standard_hours > 0 ? Math.round((w.allocated_hours / w.standard_hours) * 100) : null;

/** Billable ÷ worked hours, as a whole %; null with nothing worked. */
export const weekBillableRatio = (w: CapacityTrendWeek): number | null =>
  w.worked_hours > 0 ? Math.round((w.billable_hours / w.worked_hours) * 100) : null;

/**
 * The CSV export: one row per person per week, the same figures the heatmap
 * shows (long format, so a spreadsheet can pivot it). Leave stays "away", never
 * its type, exactly as on screen.
 */
export const capacityCsvRows = (people: readonly CapacityPerson[]): (string | number)[][] => [
  [
    'Person',
    'Title',
    'Department',
    'Freelance',
    'Week of',
    'Working days',
    'Standard hours',
    'Away hours',
    'Tentative hours',
    'Free hours',
  ],
  ...people.flatMap((p) =>
    p.weeks.map((w) => [
      p.full_name ?? '',
      p.title ?? '',
      p.department ?? '',
      p.freelance ? 'yes' : 'no',
      w.week_start,
      w.work_days,
      w.standard_hours,
      w.away_hours,
      w.tentative_hours,
      w.free_hours,
    ]),
  ),
];

/**
 * Utilisation (Talent): allocated project hours ÷ standard hours. Null when
 * there is no standard to divide by. Calculated from logged time, never typed.
 */
export const utilisationPct = (allocatedHours: number, standardHours: number): number | null =>
  standardHours > 0 ? Math.round((allocatedHours / standardHours) * 100) : null;
/** Red from 100%, amber from 85% (DESIGN-SYNC, Talent). */
export const utilisationTone = (pct: number): 'danger' | 'warning' | 'accent' =>
  pct >= 100 ? 'danger' : pct >= 85 ? 'warning' : 'accent';

/** A change between two figures: "+4", "−3", "0"; null without both. */
export const fmtDelta = (now: number | null, before: number | null): string | null => {
  if (now == null || before == null) return null;
  const d = Math.round((now - before) * 10) / 10;
  return d === 0 ? '0' : `${d > 0 ? '+' : '−'}${Math.abs(d)}`;
};

/**
 * "Free from" on a talent card: the start of the first FUTURE week (not this
 * one) whose free hours are at least half the person's standard week, the
 * fullest week in the plan. Null when no week ahead is, or there is no standard.
 */
export const freeFrom = (p: CapacityPerson): string | null => {
  const full = Math.max(0, ...p.weeks.map((w) => w.standard_hours));
  if (full <= 0) return null;
  return p.weeks.find((w, i) => i > 0 && w.free_hours >= full / 2)?.week_start ?? null;
};

export type SkillWeek = {
  week_start: string;
  standard_hours: number;
  /** Booked ÷ standard (1 = fully booked); null when nobody works that week. */
  booked: number | null;
  /** Pending leave ÷ standard; null as above. */
  tentative: number | null;
};
export type SkillLoad = { name: string; people: number; weeks: SkillWeek[] };

/**
 * The Skills × weeks heatmap: for each skill, how booked the people holding it
 * are each week — whatever isn't free (approved leave, until project bookings
 * exist) over their standard hours — with pending leave as tentative.
 */
export const skillLoad = (people: readonly CapacityPerson[]): SkillLoad[] => {
  const by = new Map<string, CapacityPerson[]>();
  for (const p of people) for (const s of p.skills) by.set(s.name, [...(by.get(s.name) ?? []), p]);
  return [...by]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, ps]) => ({
      name,
      people: ps.length,
      weeks: ps[0].weeks.map((w, i) => {
        const sum = (k: 'standard_hours' | 'free_hours' | 'tentative_hours') =>
          ps.reduce((a, p) => a + (p.weeks[i]?.[k] ?? 0), 0);
        const std = sum('standard_hours');
        return {
          week_start: w.week_start,
          standard_hours: std,
          booked: std > 0 ? (std - sum('free_hours')) / std : null,
          tentative: std > 0 ? sum('tentative_hours') / std : null,
        };
      }),
    }));
};

export type AllocRow = {
  user_id: string;
  work_date: string;
  project_id: string | null;
  seconds: number;
};

/**
 * Talent's utilisation, by the same rule: project hours logged from `from` to
 * `to` ÷ standard hours then (contract day × working days, from when they
 * joined). Only for people whose time the viewer may read; nobody else gets a
 * figure, rather than a 0 that reads as idle.
 */
export const utilisationByPerson = (
  people: readonly CapacityPerson[],
  alloc: readonly AllocRow[],
  o: {
    from: string;
    to: string;
    joined: ReadonlyMap<string, string | null>;
    workdays?: Workdays | null;
    holidays: ReadonlySet<string>;
    readable: (id: string) => boolean;
  },
): Map<string, number> => {
  const out = new Map<string, number>();
  for (const p of people) {
    if (!o.readable(p.profile_id)) continue;
    const j = o.joined.get(p.profile_id);
    const from = j && j > o.from ? j : o.from;
    const standard = p.day_hours * workingDayList(from, o.to, o.workdays, o.holidays).length;
    const logged = alloc
      .filter(
        (r) =>
          r.user_id === p.profile_id && r.project_id && r.work_date >= from && r.work_date <= o.to,
      )
      .reduce((a, r) => a + r.seconds, 0);
    const pct = utilisationPct(logged / 3600, standard);
    if (pct != null) out.set(p.profile_id, pct);
  }
  return out;
};

export type SkillReq = { skill_id: string; pct: number };
export type MixPick = { person: CapacityPerson; matched: PersonSkill[]; free: number };

/**
 * Suggest mix: people holding a skill the project needs (project_skill_reqs)
 * who have free hours this week. Best fit first: each matched skill counts the
 * project's share for it times the person's level; then the most free hours.
 */
export const suggestMix = (
  people: readonly CapacityPerson[],
  reqs: readonly SkillReq[],
): MixPick[] => {
  const need = new Map(reqs.map((r) => [r.skill_id, r.pct]));
  const fit = (m: PersonSkill[]) =>
    m.reduce((a, s) => a + (need.get(s.skill_id ?? '') ?? 0) * s.level, 0);
  return people
    .map((person) => ({
      person,
      matched: person.skills.filter((s) => s.skill_id != null && need.has(s.skill_id)),
      free: person.weeks[0]?.free_hours ?? 0,
    }))
    .filter((m) => m.matched.length > 0 && m.free > 0)
    .sort((a, b) => fit(b.matched) - fit(a.matched) || b.free - a.free);
};
